import React from 'react';
import { Info, Car, Calculator, PiggyBank, Zap, AlertTriangle, Shield, Repeat, FileText } from 'lucide-react';

/**
 * Компонент с информацией об автокредитах и советами
 */
const InfoContent = () => {
  return (
    <article className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 sm:p-8 mt-8">
      <h2 className="text-2xl font-semibold mb-6 text-gray-900 dark:text-white flex items-center gap-2">
        <Info className="w-6 h-6 text-blue-500" />
        Всё об автокредитах: особенности, виды, советы
      </h2>
      
      <div className="prose prose-indigo dark:prose-invert max-w-none prose-sm sm:prose-base">
        <h3 className="flex items-center gap-2">
          <Car className="w-5 h-5 text-indigo-500" />
          Что такое автокредит?
        </h3>
        <p>
          <strong>Автокредит</strong> — это целевой кредит, предоставляемый банком или финансовой организацией для покупки автомобиля. 
          Приобретаемый автомобиль обычно выступает в качестве залога по кредиту, что снижает риски для банка и позволяет предлагать 
          более выгодные условия по сравнению с потребительскими кредитами.
        </p>

        <h3 className="flex items-center gap-2">
          <Calculator className="w-5 h-5 text-indigo-500" />
          Ключевые параметры автокредита
        </h3>
        <ul>
          <li><strong>Стоимость автомобиля:</strong> Полная стоимость приобретаемого транспортного средства.</li>
          <li><strong>Первоначальный взнос:</strong> Часть стоимости автомобиля, которую заемщик оплачивает самостоятельно. Обычно составляет от 10% до 50% от цены автомобиля.</li>
          <li><strong>Сумма кредита:</strong> Разница между стоимостью автомобиля и первоначальным взносом.</li>
          <li><strong>Процентная ставка:</strong> Годовая ставка, по которой начисляются проценты на остаток задолженности.</li>
          <li><strong>Срок кредита:</strong> Период, на который выдается кредит, обычно от 1 до 7 лет.</li>
          <li>
            <strong>Тип платежа:</strong>
            <ul>
              <li><strong>Аннуитетный:</strong> Равные ежемесячные платежи на протяжении всего срока кредита.</li>
              <li><strong>Дифференцированный:</strong> Платежи, уменьшающиеся к концу срока кредита.</li>
            </ul>
          </li>
          <li><strong>Полная стоимость кредита (ПСК):</strong> Включает все платежи по кредиту, выраженные в годовых процентах.</li>
        </ul>

        <h3 className="flex items-center gap-2">
          <Zap className="w-5 h-5 text-orange-500" />
          Основные виды автокредитов
        </h3>
        <ul>
          <li><strong>Классический автокредит:</strong> Стандартный кредит на покупку автомобиля с залогом в виде приобретаемого транспортного средства.</li>
          <li><strong>Автокредит без первоначального взноса:</strong> Позволяет приобрести автомобиль без внесения собственных средств, но обычно имеет более высокую процентную ставку.</li>
          <li><strong>Кредит на подержанный автомобиль:</strong> Предназначен для покупки б/у автомобилей. Обычно имеет более высокую ставку и ограничения по возрасту автомобиля.</li>
          <li><strong>Льготный автокредит:</strong> Программы с государственной поддержкой, например, семейный автомобиль, первый автомобиль и т.д.</li>
          <li><strong>Автокредит с обратным выкупом (Buy-back):</strong> Позволяет снизить ежемесячные платежи за счет отложенного платежа в конце срока.</li>
          <li><strong>Экспресс-автокредит:</strong> Ускоренная процедура получения кредита с минимальным пакетом документов, но с более высокой ставкой.</li>
        </ul>

        <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-100 dark:border-blue-800 my-6">
          <h4 className="text-blue-700 dark:text-blue-300 font-medium">Как правильно оценить свои финансовые возможности?</h4>
          <p className="text-blue-600 dark:text-blue-400 text-sm mb-0">
            Оптимальная долговая нагрузка — не более 30-40% от вашего ежемесячного дохода. При расчете учитывайте все имеющиеся кредиты и займы,
            а также дополнительные расходы на содержание автомобиля: бензин, обслуживание, налоги и страховку.
          </p>
        </div>

        <h3 className="flex items-center gap-2">
          <Shield className="w-5 h-5 text-green-500" />
          Страхование при автокредитовании
        </h3>
        <p>
          При оформлении автокредита обычно требуется приобрести полис страхования. Существует два основных типа страховок:
        </p>
        <ul>
          <li>
            <strong>ОСАГО (Обязательное страхование автогражданской ответственности):</strong> Обязательная страховка, которая покрывает ущерб, 
            нанесенный другим участникам дорожного движения. Не включает страховку самого автомобиля.
          </li>
          <li>
            <strong>КАСКО (Комплексное автострахование, кроме ответственности):</strong> Добровольная страховка, которая защищает ваш автомобиль 
            от угона, повреждений в ДТП и других рисков. При автокредите банки обычно требуют оформления КАСКО, 
            поскольку автомобиль находится в залоге.
          </li>
        </ul>
        <p>
          Стоимость КАСКО может составлять 5-10% от стоимости автомобиля ежегодно, что существенно влияет на общую стоимость кредита.
          Некоторые банки предлагают сниженную процентную ставку при наличии полного страхования.
        </p>

        <h3 className="flex items-center gap-2">
          <Repeat className="w-5 h-5 text-blue-500" />
          Что такое Trade-In и как он работает
        </h3>
        <p>
          <strong>Trade-In</strong> — это схема, при которой ваш старый автомобиль принимается дилером в зачет стоимости нового.
          Этот способ имеет свои преимущества и недостатки:
        </p>
        <ul>
          <li><strong>Преимущества:</strong> Быстрая и удобная продажа старого автомобиля, сниженный первоначальный взнос, отсутствие необходимости самостоятельно искать покупателя.</li>
          <li><strong>Недостатки:</strong> Оценка автомобиля обычно на 5-15% ниже рыночной стоимости, ограничения по возрасту и состоянию принимаемых автомобилей.</li>
        </ul>
        <p>
          При использовании Trade-In сумма, вырученная от оценки вашего старого автомобиля, идет в счет первоначального взноса за новый автомобиль.
        </p>

        <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-100 dark:border-yellow-800 my-6">
          <h4 className="text-yellow-700 dark:text-yellow-300 font-medium flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" />
            На что обратить внимание при оформлении автокредита
          </h4>
          <ul className="text-yellow-600 dark:text-yellow-400 text-sm mb-0">
            <li>Внимательно читайте кредитный договор, особенно мелкий шрифт</li>
            <li>Уточняйте полную стоимость кредита (ПСК) с учетом всех комиссий и дополнительных услуг</li>
            <li>Проверьте наличие и размер штрафов за просрочку платежей</li>
            <li>Узнайте условия досрочного погашения (есть ли комиссии и ограничения)</li>
            <li>Выясните, можно ли отказаться от дополнительных услуг, которые увеличивают стоимость кредита</li>
            <li>Узнайте, как будет оформляться ПТС (на бумажном носителе или в электронном виде, в чьем залоге он будет находиться)</li>
          </ul>
        </div>

        <h3 className="flex items-center gap-2">
          <PiggyBank className="w-5 h-5 text-purple-500" />
          Досрочное погашение автокредита
        </h3>
        <p>
          Досрочное погашение — внесение суммы сверх ежемесячного платежа. Это позволяет сократить срок кредита или уменьшить ежемесячный платеж, 
          а также значительно снизить переплату по процентам.
        </p>
        <ul>
          <li>
            <strong>Уменьшение срока:</strong> Размер платежа остается прежним, но срок кредита сокращается. Это дает большую экономию на процентах.
          </li>
          <li>
            <strong>Уменьшение платежа:</strong> Срок остается прежним, но снижается ежемесячная нагрузка. Подходит, если важно снизить текущие расходы.
          </li>
        </ul>
        <p>
          Большинство банков сейчас не взимают комиссию за досрочное погашение, но могут устанавливать минимальную сумму и требовать предварительного уведомления.
        </p>

        <h3 className="flex items-center gap-2">
          <FileText className="w-5 h-5 text-amber-500" />
          Необходимые документы для автокредита
        </h3>
        <p>
          Стандартный пакет документов для получения автокредита обычно включает:
        </p>
        <ul>
          <li>Паспорт гражданина РФ</li>
          <li>Второй документ, удостоверяющий личность (СНИЛС, ИНН, загранпаспорт и т.д.)</li>
          <li>Справка о доходах (2-НДФЛ или по форме банка)</li>
          <li>Копия трудовой книжки или трудового договора</li>
          <li>Водительское удостоверение (часто требуется при оформлении автокредита)</li>
        </ul>
        <p>
          При оформлении экспресс-автокредита список документов может быть сокращен до паспорта и водительского удостоверения, 
          но это обычно сопровождается повышенной процентной ставкой.
        </p>

        <h3>Типичные дополнительные расходы при автокредитовании</h3>
        <p>
          Помимо основных платежей по кредиту, при покупке автомобиля в кредит могут возникать дополнительные расходы:
        </p>
        <ul>
          <li><strong>Страхование КАСКО и ОСАГО:</strong> Обязательные страховые полисы</li>
          <li><strong>Регистрационный сбор:</strong> Расходы на постановку автомобиля на учет в ГИБДД</li>
          <li><strong>Комиссия за выдачу кредита:</strong> Взимается некоторыми банками</li>
          <li><strong>Дополнительное оборудование:</strong> Сигнализация, защита двигателя, коврики и т.д.</li>
          <li><strong>Расширенная гарантия:</strong> Продление гарантийного срока обслуживания автомобиля</li>
          <li><strong>Кредитное страхование жизни и здоровья:</strong> Дополнительная страховка заемщика</li>
        </ul>
        <p>
          Эти расходы могут составлять значительную сумму, поэтому их необходимо учитывать при планировании бюджета на покупку автомобиля.
        </p>

        <p className="font-semibold mt-6">
          Наш калькулятор автокредита поможет вам спланировать покупку автомобиля, оценить ежемесячную нагрузку и выбрать оптимальные условия кредитования!
        </p>
      </div>
    </article>
  );
};

export default InfoContent;